/**
 * Idle Timeout Hook
 * Detects user inactivity and triggers warnings/logout
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { getIdleTimeoutConfig, minutesToMs, IdleTimeoutConfig } from '../lib/auth/idleTimeoutConfig';
import { logger } from '../lib/utils/logUtils';

interface UseIdleTimeoutOptions {
  onWarning: () => void;
  onTimeout: () => void;
  enabled?: boolean;
}

interface UseIdleTimeoutReturn {
  remainingTime: number;
  isWarning: boolean;
  resetTimer: () => void;
  config: IdleTimeoutConfig | null;
}

// Events that indicate user activity
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click'
];

export function useIdleTimeout({
  onWarning,
  onTimeout,
  enabled = true
}: UseIdleTimeoutOptions): UseIdleTimeoutReturn {
  const [config, setConfig] = useState<IdleTimeoutConfig | null>(null);
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const [isWarning, setIsWarning] = useState(false);

  const timeoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Load configuration only when enabled
  useEffect(() => {
    console.log('[useIdleTimeout] Effect triggered, enabled:', enabled);
    
    if (!enabled) {
      console.log('[useIdleTimeout] Disabled, clearing config');
      setConfig(null);
      return;
    }
    
    const loadConfig = async () => {
      try {
        console.log('[useIdleTimeout] Loading configuration...');
        const loadedConfig = await getIdleTimeoutConfig();
        setConfig(loadedConfig);
        console.log('[useIdleTimeout] ✅ Config loaded:', {
          timeout_minutes: loadedConfig.timeout_minutes,
          warning_minutes: loadedConfig.warning_minutes,
          enabled: loadedConfig.enabled
        });
        logger.info('Idle timeout config loaded', {
          timeout_minutes: loadedConfig.timeout_minutes,
          warning_minutes: loadedConfig.warning_minutes,
          enabled: loadedConfig.enabled
        });
      } catch (error) {
        console.error('[useIdleTimeout] ❌ Failed to load config:', error);
        logger.error('Failed to load idle timeout config:', error);
      }
    };

    loadConfig();
  }, [enabled]);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Reset the idle timer
  const resetTimer = useCallback(() => {
    if (!config || !config.enabled || !enabled) {
      console.log('[useIdleTimeout] resetTimer called but conditions not met:', {
        hasConfig: !!config,
        configEnabled: config?.enabled,
        hookEnabled: enabled
      });
      return;
    }

    console.log('[useIdleTimeout] Resetting timer');
    clearTimers();
    setIsWarning(false);
    lastActivityRef.current = Date.now();

    const timeoutMs = minutesToMs(config.timeout_minutes);
    const warningMs = minutesToMs(config.timeout_minutes - config.warning_minutes);

    console.log('[useIdleTimeout] Timer set:', {
      timeout_minutes: config.timeout_minutes,
      warning_minutes: config.warning_minutes,
      timeout_ms: timeoutMs,
      warning_ms: warningMs
    });

    // Set warning timer
    warningTimerRef.current = setTimeout(() => {
      // Set initial remaining time
      const warningDurationMs = minutesToMs(config.warning_minutes);
      setRemainingTime(warningDurationMs);
      setIsWarning(true);
      onWarning();
      console.log('[useIdleTimeout] ⚠️ Warning triggered');
      logger.info('Idle timeout warning triggered');

      // Start countdown for remaining time
      const warningTime = Date.now();
      countdownIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - warningTime;
        const remaining = warningDurationMs - elapsed;
        
        if (remaining <= 0) {
          setRemainingTime(0);
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
        } else {
          setRemainingTime(remaining);
        }
      }, 1000);
    }, warningMs);

    // Set timeout timer
    timeoutTimerRef.current = setTimeout(() => {
      console.log('[useIdleTimeout] ❌ Timeout triggered - logging out');
      logger.info('Idle timeout triggered - logging out user');
      onTimeout();
    }, timeoutMs);

    logger.debug('Idle timer reset', {
      timeout_minutes: config.timeout_minutes,
      warning_minutes: config.warning_minutes
    });
  }, [config, enabled, clearTimers, onWarning, onTimeout]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    // Throttle activity detection to avoid excessive resets (max once per 5 seconds)
    if (timeSinceLastActivity < 5000) return;

    lastActivityRef.current = now;

    // Always reset the timer on activity
    resetTimer();
  }, [resetTimer]);

  // Set up activity listeners
  useEffect(() => {
    console.log('[useIdleTimeout] Activity listeners effect triggered');
    
    if (!config || !config.enabled || !enabled) {
      console.log('[useIdleTimeout] Not setting up listeners:', {
        hasConfig: !!config,
        configEnabled: config?.enabled,
        hookEnabled: enabled
      });
      clearTimers();
      return;
    }
    
    console.log('[useIdleTimeout] ✅ Setting up activity listeners and initial timer');
    
    // Initial timer setup
    resetTimer();

    // Add activity event listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    
    console.log('[useIdleTimeout] Activity listeners registered:', ACTIVITY_EVENTS);

    // Cleanup
    return () => {
      console.log('[useIdleTimeout] Cleaning up timers and listeners');
      clearTimers();
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [config, enabled, resetTimer, handleActivity, clearTimers]);

  return {
    remainingTime,
    isWarning,
    resetTimer,
    config
  };
}